import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DataTable from '../../components/DataTable';
const CustomersPage = () => {
    const navigate = useNavigate();
    const [customers, setCustomers] = useState([]);
    const [salesManagers, setSalesManagers] = useState([]);
    const [factories, setFactories] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const fetchCustomers = async () => {
        setIsLoading(true);
        setError('');
        try {
            const response = await fetch('/api/customers', {
                credentials: 'include',
            });
            if (response.status === 401) {
                navigate('/login');
                return;
            }
            const result = await response.json();
            setCustomers(result.data || []);
        }
        catch (err) {
            setError(err.message || 'Failed to load customers');
        }
        finally {
            setIsLoading(false);
        }
    };
    const fetchSalesManagers = async () => {
        try {
            const response = await fetch('/api/sales-managers', {
                credentials: 'include',
            });
            if (response.ok) {
                const result = await response.json();
                setSalesManagers(result);
            }
        }
        catch (err) {
            console.error('Failed to load sales managers:', err);
        }
    };
    const fetchFactories = async () => {
        try {
            const response = await fetch('/api/pricing/factories', {
                credentials: 'include',
            });
            if (response.ok) {
                const result = await response.json();
                setFactories(result.data || []);
            }
        }
        catch (err) {
            console.error('Failed to load factories:', err);
        }
    };
    useEffect(() => {
        fetchCustomers();
        fetchSalesManagers();
        fetchFactories();
    }, []);
    const handleAdd = async (newRow) => {
        try {
            // Clean up data based on account type
            const cleanedData = { ...newRow };
            if (cleanedData.account_type === 'parent') {
                cleanedData.parent_customer_id = null;
            }
            if (!cleanedData.account_type) {
                cleanedData.account_type = 'parent';
            }
            const response = await fetch('/api/customers', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify(cleanedData),
            });
            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.error || 'Failed to add customer');
            }
            await fetchCustomers();
        }
        catch (err) {
            alert(err.message);
            throw err;
        }
    };
    const handleUpdate = async (id, updatedRow) => {
        try {
            // Clean up data based on account type
            const cleanedData = { ...updatedRow };
            if (cleanedData.account_type === 'parent') {
                cleanedData.parent_customer_id = null;
            }
            const response = await fetch(`/api/customers/${id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify(cleanedData),
            });
            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.error || 'Failed to update customer');
            }
            await fetchCustomers();
        }
        catch (err) {
            alert(err.message);
            throw err;
        }
    };
    const handleDelete = async (id) => {
        try {
            const response = await fetch(`/api/customers/${id}`, {
                method: 'DELETE',
                credentials: 'include',
            });
            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.error || 'Failed to delete customer');
            }
            await fetchCustomers();
        }
        catch (err) {
            alert(err.message);
            throw err;
        }
    };
    // Get only parent accounts for the parent selector dropdown
    const parentCustomers = customers.filter(c => c.account_type === 'parent');
    const columns = [
        {
            key: 'name',
            label: 'Customer Name',
            type: 'text',
            required: true,
            width: 'w-56',
        },
        {
            key: 'account_number',
            label: 'Account Number',
            type: 'text',
            width: 'w-40',
        },
        {
            key: 'account_type',
            label: 'Account Type',
            type: 'select',
            required: true,
            options: [
                { value: 'parent', label: 'Parent' },
                { value: 'child', label: 'Child' },
            ],
            width: 'w-32',
        },
        {
            key: 'parent_customer_id',
            label: 'Parent Account',
            type: 'select',
            options: parentCustomers.map((c) => ({ value: c.id, label: c.name })),
            width: 'w-48',
            render: (value, row) => row.parent_customer_name || '-',
            // This field is only shown/required when account_type is 'child'
        },
        {
            key: 'sales_manager_id',
            label: 'Sales Manager',
            type: 'select',
            options: salesManagers.map((sm) => ({ value: sm.id, label: sm.name })),
            width: 'w-48',
            render: (value, row) => row.sales_manager_name || '-',
        },
        {
            key: 'factory_id',
            label: 'Factory',
            type: 'select',
            options: factories.map((f) => ({ value: f.id, label: f.name })),
            width: 'w-40',
            render: (value, row) => row.factory_name || '-',
        },
        {
            key: 'email',
            label: 'Email',
            type: 'text',
            width: 'w-56',
        },
        {
            key: 'phone',
            label: 'Phone',
            type: 'text',
            width: 'w-40',
        },
        {
            key: 'address',
            label: 'General Address',
            type: 'text',
            width: 'w-64',
        },
        {
            key: 'bill_to_address',
            label: 'Bill To Address',
            type: 'text',
            width: 'w-64',
        },
        {
            key: 'bill_to_location_type',
            label: 'Bill To Location',
            type: 'select',
            options: [
                { value: 'main office', label: 'Main Office' },
                { value: 'showroom', label: 'Showroom' },
                { value: 'warehouse', label: 'Warehouse' },
            ],
            width: 'w-40',
        },
        {
            key: 'ship_to_address',
            label: 'Ship To Address',
            type: 'text',
            width: 'w-64',
        },
        {
            key: 'ship_to_location_type',
            label: 'Ship To Location',
            type: 'select',
            options: [
                { value: 'main office', label: 'Main Office' },
                { value: 'showroom', label: 'Showroom' },
                { value: 'warehouse', label: 'Warehouse' },
            ],
            width: 'w-40',
        },
        {
            key: 'notes',
            label: 'Notes',
            type: 'text',
            width: 'w-64',
        },
    ];
    return (_jsxs("div", { className: "min-h-screen bg-gradient-to-br from-primary-50 via-white to-accent-50", children: [_jsx("nav", { className: "bg-white shadow-sm border-b border-secondary-200", children: _jsx("div", { className: "container-custom py-4", children: _jsxs("div", { className: "flex items-center justify-between", children: [_jsxs("div", { className: "flex items-center space-x-4", children: [_jsx("button", { onClick: () => navigate('/dashboard'), className: "text-secondary-600 hover:text-secondary-900", children: "\u2190 Back to Dashboard" }), _jsx("h1", { className: "text-2xl font-bold text-secondary-900", children: "Customers" })] }), _jsx("button", { onClick: () => navigate('/logout'), className: "btn btn-outline text-sm py-2", children: "Logout" })] }) }) }), _jsx("main", { className: "container-custom py-8", children: _jsxs("div", { className: "card", children: [error && (_jsx("div", { className: "mb-4 p-3 bg-error-50 border border-error-200 text-error-700 rounded-lg text-sm", children: error })), _jsx(DataTable, { columns: columns, data: customers, onAdd: handleAdd, onUpdate: handleUpdate, onDelete: handleDelete, isLoading: isLoading, emptyMessage: "No customers found. Click 'Add New' to create one." })] }) })] }));
};
export default CustomersPage;
